"use client";

// ============================================================
// ANSICHTSSACHE-AGENT – Farbe des Kuerzel-Kreises waehlen
// ============================================================
// Der Kreis ist zugleich der Knopf: ein Klick oeffnet den
// Waehler. Oben die Palette — jedes Feld zeigt das Kuerzel der
// Person schon in seiner Farbe —, darunter der freie Waehler:
// Flaeche fuer Saettigung und Helligkeit, Regler fuer den
// Farbton, Feld fuer den Farbwert. Erst gab es nur die Palette;
// „nicht nur Presets, sondern wirklich einen Colorpicker“
// (24.09.2026).
//
// Zwei Stellen benutzen ihn, darum steht er hier und nicht mehr
// neben der Personalakte: der Stammdaten-Dialog einer Person und
// seit 27.09.2026 Mein Konto › Profil, wo jede Person ihre eigene
// Farbe waehlt — ohne das Recht, die Personalakte zu aendern.
//
// WANN DIE WAHL GILT:
//   Palette          mit dem Klick, und der Waehler geht zu
//   Flaeche, Regler  beim Loslassen; waehrend des Ziehens laeuft
//                    nur die Vorschau mit (Kreis oben und Kreis
//                    neben dem Farbwert)
//   Farbwert         mit der Eingabetaste oder beim Verlassen
//   Klick daneben    behaelt, was eingestellt ist
//   Escape           nimmt zurueck und schliesst nur den Waehler,
//                    nicht den Dialog darunter
//
// GESPEICHERT wird nie hier, sondern vom Formular drumherum — so
// wie jedes andere Feld darin: im Stammdaten-Dialog steht die Wahl
// in einem verborgenen Feld (`name`) und geht mit „Speichern“ des
// Modals hinaus, im Profil meldet `onChange` sie an die
// Aenderungsleiste.
//
// Der Name der Farbe steht UNTER der Palette und wechselt mit dem
// Zeiger. Ein `title` waere das Systemfenster, das im Haus sonst
// nirgends vorkommt.
// ============================================================

import { useEffect, useRef, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Check, Palette } from "lucide-react";
import { Avatar } from "./avatar";
import {
  DEFAULT_AVATAR_CLASSES,
  PERSON_COLOR_PRESETS,
  avatarColorClasses,
  avatarColorStyle,
  hexToHsv,
  hsvToHex,
  normalizePersonColor,
  personColor,
  presetLabel,
  type Hsv,
} from "@/lib/person-colors";
import { cn, getInitials } from "@/lib/utils";

/** So viele Felder je Zeile — 18 Farben ergeben drei volle Reihen. */
const COLUMNS = 6;

/** Womit der freie Waehler beginnt, solange keine Farbe gewaehlt ist. */
const START = "#0e7490";

export function AvatarColorPicker({
  firstName,
  lastName,
  color,
  editable = true,
  name,
  onChange,
  size = "lg",
  disabled,
  id,
}: {
  firstName: string;
  lastName: string;
  color: string | null;
  editable?: boolean;
  /** Die Farbe geht in einem verborgenen Feld dieses Namens mit der FormData hinaus. */
  name?: string;
  /** Die Wahl geht an den Aufrufer (`null` = Standard); gespeichert wird dort. */
  onChange?: (color: string | null) => void;
  /** `md` in einer Formularzeile neben Eingabefeldern, `lg` im Kopf eines Dialogs. */
  size?: "md" | "lg";
  disabled?: boolean;
  /** Fuer ein `<label htmlFor>` davor: ein Klick auf die Beschriftung oeffnet den Waehler. */
  id?: string;
}) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);

  // Was gilt: die Wahl, bis von aussen ein neuer Stand kommt. Kommt
  // von dort etwas anderes (ein Kollege hat umgefaerbt, das Formular
  // wurde verworfen), gilt das.
  const saved = personColor(color);
  const [chosen, setChosen] = useState(saved);
  const [fromServer, setFromServer] = useState(saved);
  if (saved !== fromServer) {
    setFromServer(saved);
    setChosen(saved);
  }

  // Der Entwurf des freien Waehlers. Das Ref traegt denselben Wert
  // ohne Umweg ueber das Rendern: das Loslassen kann schneller
  // kommen als der Bildaufbau nach der letzten Bewegung.
  const [hsv, setHsv] = useState<Hsv>(() => hexToHsv(saved ?? START));
  const [hexText, setHexText] = useState(saved ?? START);
  const [draft, setDraft] = useState<string | null>(null);
  const draftRef = useRef<string | null>(null);

  const gridRef = useRef<HTMLDivElement>(null);
  const shown = draft ?? chosen;
  const initials = getInitials(firstName, lastName);

  function setDraftValue(hex: string | null) {
    draftRef.current = hex;
    setDraft(hex);
  }

  /** Uebernimmt eine Farbe (`null` = Vorgabe) — sofort im Kreis sichtbar. */
  function commit(next: string | null) {
    setDraftValue(null);
    if (next === chosen) return;
    setChosen(next);
    onChange?.(next);
  }

  /** Den Entwurf uebernehmen, falls es einen gibt. */
  function commitDraft() {
    if (draftRef.current) commit(draftRef.current);
  }

  function changeHsv(next: Hsv) {
    setHsv(next);
    const hex = hsvToHex(next);
    setHexText(hex);
    setDraftValue(hex);
  }

  function handleOpenChange(next: boolean) {
    if (next) {
      // Der freie Waehler steht beim Oeffnen auf der gespeicherten
      // Farbe — nicht auf dem, was beim letzten Mal verworfen wurde.
      setHsv(hexToHsv(chosen ?? START));
      setHexText(chosen ?? START);
      setDraftValue(null);
    } else {
      // Ein Klick daneben behaelt, was eingestellt ist.
      commitDraft();
      setHovered(null);
    }
    setOpen(next);
  }

  // Escape nimmt zurueck und schliesst den Waehler, nicht den
  // Dialog darunter — am Dokument in der Auffangphase, wie beim
  // Auswahlfeld (`components/ui/select.tsx`, dort steht, warum es
  // anders nicht traegt).
  useEffect(() => {
    if (!open) return;
    function catchEscape(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      setDraftValue(null);
      setHovered(null);
      setOpen(false);
    }
    document.addEventListener("keydown", catchEscape, true);
    return () => document.removeEventListener("keydown", catchEscape, true);
  }, [open]);

  if (!editable) {
    return <Avatar firstName={firstName} lastName={lastName} color={chosen} size={size} />;
  }

  /** Pfeiltasten wandern durch die Palette, Pos1/Ende an ihre Enden. */
  function handleGridKey(e: React.KeyboardEvent<HTMLDivElement>) {
    const swatches = Array.from(
      gridRef.current?.querySelectorAll<HTMLButtonElement>("button[data-swatch]") ?? [],
    );
    const index = swatches.indexOf(document.activeElement as HTMLButtonElement);
    if (index < 0) return;

    const step: Record<string, number> = {
      ArrowRight: 1,
      ArrowLeft: -1,
      ArrowDown: COLUMNS,
      ArrowUp: -COLUMNS,
    };
    let next: number | null = null;
    if (e.key in step) next = index + step[e.key];
    if (e.key === "Home") next = 0;
    if (e.key === "End") next = swatches.length - 1;
    if (next === null) return;

    e.preventDefault();
    swatches[Math.min(Math.max(next, 0), swatches.length - 1)]?.focus();
  }

  function handleHexInput(value: string) {
    setHexText(value);
    const hex = normalizePersonColor(value);
    if (!hex) return;
    setHsv(hexToHsv(hex));
    setDraftValue(hex);
  }

  const label = hovered ?? (shown ? (presetLabel(shown) ?? shown) : "Standard");

  return (
    <>
      {name && <input type="hidden" name={name} value={chosen ?? ""} />}
      <Popover.Root open={open} onOpenChange={handleOpenChange}>
        <Popover.Trigger asChild>
          <button
            id={id}
            type="button"
            aria-label="Farbe des Kürzels wählen"
            disabled={disabled}
            className="group focus-visible:ring-accent/40 relative shrink-0 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-60"
          >
            <Avatar
              firstName={firstName}
              lastName={lastName}
              color={shown}
              size={size}
              hint={false}
              className="group-hover:ring-border-control ring-2 ring-transparent transition-shadow"
            />
            {/* Das Zeichen sagt, was der Klick tut: hier wird
              gefaerbt, nicht ein Bild hochgeladen. */}
            <span
              aria-hidden
              className="bg-bg-primary text-text-secondary ring-border group-hover:text-text-primary absolute -right-1 -bottom-1 grid h-5 w-5 place-items-center rounded-full ring-1 transition-colors"
            >
              <Palette className="h-3 w-3" />
            </span>
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            align="start"
            sideOffset={8}
            collisionPadding={12}
            onOpenAutoFocus={(e) => {
              // Der Fokus steht auf der gewaehlten Farbe, nicht auf
              // der ersten — so geht es mit den Pfeilen von dort aus.
              e.preventDefault();
              const selected = gridRef.current?.querySelector<HTMLButtonElement>(
                'button[aria-pressed="true"]',
              );
              (selected ?? gridRef.current?.querySelector<HTMLButtonElement>("button"))?.focus();
            }}
            className="border-border bg-bg-primary shadow-dropdown z-50 w-[19rem] rounded-xl border p-3"
          >
            <p className="text-overline text-text-tertiary px-0.5 pb-2.5">Palette</p>

            <div
              ref={gridRef}
              role="group"
              aria-label="Palette"
              onKeyDown={handleGridKey}
              onMouseLeave={() => setHovered(null)}
              className="grid grid-cols-6 gap-2"
            >
              {PERSON_COLOR_PRESETS.map((preset) => {
                const selected = preset.hex === shown;
                return (
                  <button
                    key={preset.hex}
                    type="button"
                    data-swatch
                    aria-label={preset.label}
                    aria-pressed={selected}
                    onClick={() => {
                      commit(preset.hex);
                      setHovered(null);
                      setOpen(false);
                    }}
                    onMouseEnter={() => setHovered(preset.label)}
                    onFocus={() => setHovered(preset.label)}
                    onBlur={() => setHovered(null)}
                    className={cn(
                      "text-caption relative grid h-9 w-9 place-items-center rounded-full font-medium outline-none",
                      "ring-offset-bg-primary duration-fast transition-[box-shadow,transform] hover:scale-110",
                      "focus-visible:ring-text-secondary focus-visible:ring-2 focus-visible:ring-offset-2",
                      selected && "ring-text-primary ring-2 ring-offset-2",
                      avatarColorClasses(preset.hex),
                    )}
                    style={avatarColorStyle(preset.hex)}
                  >
                    {selected ? <Check className="h-4 w-4" aria-hidden /> : initials}
                  </button>
                );
              })}
            </div>

            <div className="border-border-subtle mt-3 space-y-3 border-t pt-3">
              <p className="text-overline text-text-tertiary px-0.5">Eigene Farbe</p>

              <SaturationArea hsv={hsv} onChange={changeHsv} onCommit={commitDraft} />
              <HueSlider hsv={hsv} onChange={changeHsv} onCommit={commitDraft} />

              <div className="flex items-center gap-2.5">
                <span
                  aria-hidden
                  className={cn(
                    "text-body-sm grid h-9 w-9 shrink-0 place-items-center rounded-full font-medium",
                    avatarColorClasses(draft ?? hexText),
                  )}
                  style={avatarColorStyle(draft ?? hexText)}
                >
                  {initials}
                </span>
                <input
                  aria-label="Farbwert"
                  value={hexText}
                  spellCheck={false}
                  maxLength={7}
                  onChange={(e) => handleHexInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commitDraft();
                    }
                  }}
                  onBlur={() => {
                    // Ein halb getippter Wert bleibt nicht stehen: das
                    // Feld zeigt danach wieder, was gilt.
                    if (!normalizePersonColor(hexText)) setHexText(shown ?? START);
                    commitDraft();
                  }}
                  className={cn(
                    "bg-bg-primary text-body-sm text-text-primary h-9 w-full min-w-0 rounded-lg border px-3 font-mono uppercase",
                    "border-border-control focus:border-accent focus:ring-accent/20 outline-none focus:ring-2",
                    !normalizePersonColor(hexText) && "border-danger focus:border-danger",
                  )}
                />
              </div>
            </div>

            <div className="border-border-subtle mt-3 flex items-center justify-between gap-3 border-t pt-3">
              <button
                type="button"
                aria-pressed={shown === null}
                onClick={() => {
                  commit(null);
                  setHovered(null);
                  setOpen(false);
                }}
                onMouseEnter={() => setHovered("Standard")}
                onMouseLeave={() => setHovered(null)}
                onFocus={() => setHovered("Standard")}
                onBlur={() => setHovered(null)}
                className="text-body-sm text-text-secondary hover:text-text-primary hover:bg-bg-secondary focus-visible:ring-accent/40 -ml-1 flex h-8 items-center gap-2 rounded-lg px-1 pr-2 outline-none focus-visible:ring-2"
              >
                <span
                  aria-hidden
                  className={cn(
                    "text-caption grid h-6 w-6 place-items-center rounded-full font-medium",
                    DEFAULT_AVATAR_CLASSES,
                    shown === null &&
                      "ring-text-primary ring-offset-bg-primary ring-2 ring-offset-1",
                  )}
                >
                  {shown === null ? <Check className="h-3 w-3" /> : initials.slice(0, 1)}
                </span>
                Standard
              </button>
              {/* Ein Farbwert steht in Schreibmaschinenschrift, ein
                Name in der Schrift des Hauses. */}
              <span
                className={cn(
                  "text-caption text-text-tertiary truncate",
                  label.startsWith("#") && "font-mono uppercase",
                )}
                aria-live="polite"
              >
                {label}
              </span>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </>
  );
}

const clamp = (value: number) => Math.min(Math.max(value, 0), 1);

/**
 * Die Flaeche: nach rechts satter, nach oben heller. Ziehen mit
 * Maus, Finger oder Stift (`setPointerCapture` haelt den Zeiger
 * auch ausserhalb der Flaeche fest), dazu Pfeiltasten — mit
 * Umschalt in groesseren Schritten.
 */
function SaturationArea({
  hsv,
  onChange,
  onCommit,
}: {
  hsv: Hsv;
  onChange: (next: Hsv) => void;
  onCommit: () => void;
}) {
  function fromPointer(e: React.PointerEvent<HTMLDivElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    onChange({
      ...hsv,
      s: clamp((e.clientX - rect.left) / rect.width),
      v: clamp(1 - (e.clientY - rect.top) / rect.height),
    });
  }

  function handleKey(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      onCommit();
      return;
    }
    const step = e.shiftKey ? 0.1 : 0.01;
    const moves: Record<string, Partial<Hsv>> = {
      ArrowRight: { s: clamp(hsv.s + step) },
      ArrowLeft: { s: clamp(hsv.s - step) },
      ArrowUp: { v: clamp(hsv.v + step) },
      ArrowDown: { v: clamp(hsv.v - step) },
    };
    const move = moves[e.key];
    if (!move) return;
    e.preventDefault();
    onChange({ ...hsv, ...move });
  }

  return (
    <div
      role="slider"
      tabIndex={0}
      aria-label="Sättigung und Helligkeit"
      aria-valuenow={Math.round(hsv.s * 100)}
      aria-valuetext={`Sättigung ${Math.round(hsv.s * 100)} %, Helligkeit ${Math.round(hsv.v * 100)} %`}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        e.currentTarget.focus();
        fromPointer(e);
      }}
      onPointerMove={(e) => {
        if (e.currentTarget.hasPointerCapture(e.pointerId)) fromPointer(e);
      }}
      onPointerUp={onCommit}
      onKeyDown={handleKey}
      onBlur={onCommit}
      className="focus-visible:ring-accent/40 relative h-32 w-full cursor-crosshair touch-none rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
      style={{ backgroundColor: `hsl(${hsv.h} 100% 50%)` }}
    >
      <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-white to-transparent" />
      <div className="absolute inset-0 rounded-lg bg-gradient-to-t from-black to-transparent" />
      <div className="absolute inset-0 rounded-lg ring-1 ring-black/10 ring-inset" />
      <span
        aria-hidden
        className="pointer-events-none absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.25),0_1px_3px_rgba(0,0,0,0.3)]"
        style={{
          left: `${hsv.s * 100}%`,
          top: `${(1 - hsv.v) * 100}%`,
          backgroundColor: hsvToHex(hsv),
        }}
      />
    </div>
  );
}

/** Der Farbton, einmal um den Kreis. */
function HueSlider({
  hsv,
  onChange,
  onCommit,
}: {
  hsv: Hsv;
  onChange: (next: Hsv) => void;
  onCommit: () => void;
}) {
  function fromPointer(e: React.PointerEvent<HTMLDivElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    onChange({ ...hsv, h: clamp((e.clientX - rect.left) / rect.width) * 360 });
  }

  function handleKey(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      onCommit();
      return;
    }
    const step = e.shiftKey ? 15 : 1;
    const moves: Record<string, number> = {
      ArrowRight: step,
      ArrowUp: step,
      ArrowLeft: -step,
      ArrowDown: -step,
    };
    if (!(e.key in moves)) return;
    e.preventDefault();
    onChange({ ...hsv, h: Math.min(Math.max(hsv.h + moves[e.key], 0), 360) });
  }

  return (
    <div
      role="slider"
      tabIndex={0}
      aria-label="Farbton"
      aria-valuemin={0}
      aria-valuemax={360}
      aria-valuenow={Math.round(hsv.h)}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        e.currentTarget.focus();
        fromPointer(e);
      }}
      onPointerMove={(e) => {
        if (e.currentTarget.hasPointerCapture(e.pointerId)) fromPointer(e);
      }}
      onPointerUp={onCommit}
      onKeyDown={handleKey}
      onBlur={onCommit}
      className="focus-visible:ring-accent/40 relative h-3 w-full cursor-pointer touch-none rounded-full outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
      style={{
        background:
          "linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)",
      }}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.25),0_1px_3px_rgba(0,0,0,0.3)]"
        style={{ left: `${(hsv.h / 360) * 100}%`, backgroundColor: `hsl(${hsv.h} 100% 50%)` }}
      />
    </div>
  );
}
